/**
 * LPCRandomAppearance — Generates random valid LPC character appearances.
 *
 * Used for spawning visually varied NPCs. Supports deterministic generation
 * via SeededRandom so multiplayer peers can reproduce the same appearance
 * from just a seed value.
 */

import SeededRandom from '../utils/SeededRandom.js';
import {
  BODY_TYPE_LIST, SKIN_SWATCHES, HAIR_SWATCHES, EYE_SWATCHES,
  getItemsForCategory, getManifest
} from './LPCLayerDefinitions.js';

/**
 * Generate a random LPC character appearance.
 *
 * @param {Object} [options]
 * @param {string} [options.bodyType] - Force body type (e.g., 'human-male'). Random if omitted.
 * @param {number} [options.seed] - Deterministic seed for reproducible results.
 * @param {boolean} [options.minimal] - If true, only body + head + eyes (faster composition).
 * @param {string} [options.skinColor] - Force skin color. Random if omitted.
 * @param {string} [options.hairColor] - Force hair color. Random if omitted.
 * @returns {Object} Appearance config compatible with buildLayerStack() / setLPCAppearance()
 */
export function generateRandomAppearance(options = {}) {
  const manifest = getManifest();
  if (!manifest) throw new Error('Manifest not loaded. Call loadManifest() first.');

  const rng = new SeededRandom(options.seed !== undefined ? options.seed : (Math.random() * 0x7FFFFFFF) | 0);

  // 1. Body type
  const bodyType = options.bodyType || pick(BODY_TYPE_LIST, rng).id;
  const bodyKey = BODY_TYPE_LIST.find(b => b.id === bodyType)?.bodyKey || 'male';

  // 2. Colors (use forced values if provided, otherwise random)
  const skinColors = Object.keys(SKIN_SWATCHES);
  const hairColors = Object.keys(HAIR_SWATCHES);
  const eyeColors = Object.keys(EYE_SWATCHES);

  const skinColor = options.skinColor || pick(skinColors, rng);
  const hairColor = options.hairColor || pick(hairColors, rng);
  const eyeColor = pick(eyeColors, rng);

  // 3. Head (required — pick a compatible one)
  const heads = getItemsForCategory('head', bodyKey);
  const head = heads.length > 0 ? pick(heads, rng).id : 'heads_human_male';

  if (options.minimal) {
    return {
      bodyType, skinColor, head,
      face: null,
      eyes: maybePick('eyes', bodyKey, rng, 0.7),
      eyeColor,
      eyebrows: null, eyebrowColor: hairColor,
      hair: null, hairColor,
      hairext: null,
      beard: null, beardColor: hairColor,
      ears: null, nose: null,
      facial: null, facialVariant: null,
      horns: null, tail: null, wings: null
    };
  }

  // 4. Optional features with probability weights
  return {
    bodyType,
    skinColor,
    head,
    face: null,
    eyes: maybePick('eyes', bodyKey, rng, 0.7),
    eyeColor,
    eyebrows: maybePick('eyebrows', bodyKey, rng, 0.5),
    eyebrowColor: hairColor,
    hair: maybePick('hair', bodyKey, rng, 0.85),
    hairColor,
    hairext: maybePick('hairext', bodyKey, rng, 0.1),
    beard: maybePick('beard', bodyKey, rng, 0.15),
    beardColor: hairColor,
    ears: maybePick('ears', bodyKey, rng, 0.1),
    nose: maybePick('nose', bodyKey, rng, 0.08),
    facial: null,
    facialVariant: null,
    horns: null,
    tail: null,
    wings: null
  };
}

function pick(arr, rng) {
  return arr[rng.nextInt(0, arr.length)];
}

function maybePick(category, bodyKey, rng, chance) {
  if (rng.next() > chance) return null;
  const items = getItemsForCategory(category, bodyKey);
  if (items.length === 0) return null;
  return pick(items, rng).id;
}
